import { Core, Decorative } from "@faire/design-tokens";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web/common/transform-images";
import { Column } from "@faire/web/slate/Layout";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

const DESKTOP_IMAGE = (strictLocalize: StrictLocalizeFunction): IImage =>
  IImage.build({
    url: "https://cdn.faire.com/fastly/11d4f8229e746500502c8418be3ecde6b84c528d82fd028dd3a4de71422ee76e.png",
    width: 1280,
    alt_text: strictLocalize({
      defaultMessage: "People chatting in a Faire office",
      description: {
        text: "Alt text for an image of people chatting in a Faire office",
      },
    }),
  });
const TABLET_IMAGE = (strictLocalize: StrictLocalizeFunction): IImage =>
  IImage.build({
    url: "https://cdn.faire.com/fastly/f700fbd391d842e2ae1cb9e7b496181c2efc34ac8be4a94e2efc873d7a1326d0.png",
    width: 768,
    alt_text: strictLocalize({
      defaultMessage: "People chatting in a Faire office",
      description: {
        text: "Alt text for an image of people chatting in a Faire office",
      },
    }),
  });
const MOBILE_IMAGE = (strictLocalize: StrictLocalizeFunction): IImage =>
  IImage.build({
    url: "https://cdn.faire.com/fastly/3869318619d5f5c68c21822a317d4bc83f6f649bdba8dfac0e7fc9667e328491.png",
    width: 375,
    alt_text: strictLocalize({
      defaultMessage: "People chatting in a Faire office",
      description: {
        text: "Alt text for an image of people chatting in a Faire office",
      },
    }),
  });

const BelongingPageLargeImageBase = (
  strictLocalize: StrictLocalizeFunction
) => {
  const image: IImage = useResponsiveValue({
    mobileAndAbove: MOBILE_IMAGE(strictLocalize),
    tabletAndAbove: TABLET_IMAGE(strictLocalize),
    desktopAndAbove: DESKTOP_IMAGE(strictLocalize),
  });

  const isSSR = useIsSSR();

  return (
    <BackgroundColorWrapper>
      <ImageFixedWidth
        data-test-id="BelongingLargeImage"
        src={optimizeImageUrl(image, isSSR, { width: image.width })}
      />
    </BackgroundColorWrapper>
  );
};

export const BelongingPageLargeImage = (
  strictLocalize: StrictLocalizeFunction
) => observer(() => BelongingPageLargeImageBase(strictLocalize));

const BackgroundColorWrapper = styled(Column).attrs({ align: "center" })`
  ${responsiveMediaQueries.tabletAndAbove} {
    background: linear-gradient(
      to bottom,
      ${Decorative.tint.neutral} 0%,
      ${Decorative.tint.neutral} 50%,
      ${Core.surface.inverse} 50%,
      ${Core.surface.inverse} 100%
    );
  }
`;

const ImageFixedWidth = styled.img`
  width: 100%;
  max-width: 1280px;
`;
